import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext({});

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Check localStorage first, fallback to 'light' as default to show black background
    const savedTheme = localStorage.getItem('idartists-theme');
    return savedTheme || 'light';
  });

  // Apply theme to document and save to localStorage
  useEffect(() => {
    document.documentElement?.setAttribute('data-theme', theme);
    localStorage.setItem('idartists-theme', theme);
  }, [theme]);

  // Handle system theme changes and window focus events
  useEffect(() => {
    const handleFocus = () => {
      // On window focus, maintain the stored theme preference
      const storedTheme = localStorage.getItem('idartists-theme') || 'light';
      if (storedTheme !== theme) {
        setTheme(storedTheme);
      }
    };

    const handleVisibilityChange = () => {
      // Maintain theme consistency when tab becomes visible again
      if (!document.hidden) {
        const storedTheme = localStorage.getItem('idartists-theme') || 'light';
        if (storedTheme !== theme) {
          setTheme(storedTheme);
        }
      }
    };

    // Listen for window focus and visibility changes
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [theme]);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
  };

  const setLightMode = () => {
    setTheme('light');
  };

  const setDarkMode = () => {
    setTheme('dark');
  };

  const value = {
    theme,
    toggleTheme,
    setLightMode,
    setDarkMode,
    isDark: theme === 'dark',
    isLight: theme === 'light'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};